import {
  Bar,
  BarChart,
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Dialog } from "../../components/Dialog";
import { Switch } from "../../components/Switch";
import type { ColumnMetadata, HotelMetricsWidget } from "../specs/hotelMetrics";
import { formatDateDisplay, formatMetricValue } from "../specs/hotelMetrics";

export type ChartType = 'bar' | 'line';

interface HotelMetricsExpandedModalProps {
  data: HotelMetricsWidget;
  columnMetadata: ColumnMetadata;
  chartData: Array<{
    date: string;
    stlyDate?: string;
    dayLabel: string;
    current: number | null;
    stly?: number;
  }>;
  showSTLY: boolean;
  onToggleSTLY: (checked: boolean) => void;
  onClose: () => void;
  currentSummary?: number;
  stlySummary?: number;
  comparisonDelta?: number;
  CustomTooltip: React.ComponentType<any>;
  CustomLegend: React.ComponentType;
  getShortDisplayName: (column: ColumnMetadata) => string;
  chartType?: ChartType;
}

// Palette derived from the Claude-Design kit tokens — `var(--d-*)`
// values inlined as hex so Recharts SVG fills resolve without runtime
// CSS-var lookups.
const CHART_COLORS = {
  current: "#4D5B6E", // --d-accent-deep (slate ink)
  currentLight: "#6F7E92", // --d-accent (slate, used for the diagonal stripe over `current`)
  stly: "#A8625C", // --d-neg (warm clay — sits opposite the slate Current without screaming "error")
  stlyLight: "#C2837E", // 75% mix of --d-neg + white, for the stripe
  grid: "rgba(28,26,22,0.09)", // --d-rule
  text: "#5C5B57", // --d-ink-muted
  border: "rgba(28,26,22,0.09)", // --d-rule
};

/**
 * Hotel Metrics Expanded Modal
 *
 * Full-screen modal overlay for viewing charts in larger format.
 * Features:
 * - Dark overlay with centered modal
 * - 500px tall chart (vs 180-200px compact)
 * - Click outside or X button to close
 * - STLY toggle functional in expanded view
 * - Supports both bar and line chart types
 */
export function HotelMetricsExpandedModal({
  data,
  columnMetadata,
  chartData,
  showSTLY,
  onToggleSTLY,
  onClose,
  currentSummary,
  stlySummary,
  comparisonDelta,
  CustomTooltip,
  CustomLegend,
  getShortDisplayName,
  chartType = 'bar',
}: HotelMetricsExpandedModalProps) {
  return (
    <Dialog
      variant="center"
      size="xl"
      zIndexClassName="z-[9999]"
      onClose={onClose}
      closeAriaLabel="Close expanded view"
      title={getShortDisplayName(columnMetadata)}
      headerExtra={
        data.stly.enabled &&
        data.stly.timeSeries &&
        data.stly.timeSeries.length > 0 ? (
          <div className="flex items-center gap-2">
            <Switch
              checked={showSTLY}
              onChange={(e) => onToggleSTLY(e.target.checked)}
            />
            <label
              className="cursor-pointer"
              style={{
                fontFamily: "var(--d-font-sans)",
                fontSize: 12.5,
                color: "var(--d-ink-muted)",
              }}
            >
              vs STLY
            </label>
          </div>
        ) : undefined
      }
    >
        {/* Modal Content */}
        <div className="space-y-6">
          {/* Summary Metrics — cream-soft wash so it reads as inset
              kit chrome rather than a Tailwind surface-tertiary slab. */}
          <div
            className="flex items-center gap-3 flex-wrap rounded-lg px-4 py-3"
            style={{
              background: "var(--d-cream-soft)",
              border: "1px solid var(--d-rule-soft)",
            }}
          >
            {/* STLY */}
            {data.stly.enabled && stlySummary !== undefined ? (
              <>
                <span
                  style={{
                    fontFamily: "var(--d-font-sans)",
                    fontSize: 12.5,
                    color: "var(--d-ink-muted)",
                  }}
                >
                  STLY:
                </span>
                <span
                  style={{
                    fontFamily: "var(--d-font-sans)",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "var(--d-ink)",
                    letterSpacing: "-0.005em",
                  }}
                >
                  {formatMetricValue(stlySummary, columnMetadata)}
                </span>
                <span style={{ color: "var(--d-ink-ghost)" }}>|</span>
              </>
            ) : null}

            {/* Current */}
            <span
              style={{
                fontFamily: "var(--d-font-sans)",
                fontSize: 12.5,
                color: "var(--d-ink-muted)",
              }}
            >
              Current:
            </span>
            <span
              style={{
                fontFamily: "var(--d-font-sans)",
                fontSize: 16,
                fontWeight: 600,
                color: "var(--d-ink)",
                letterSpacing: "-0.005em",
              }}
            >
              {currentSummary !== undefined
                ? formatMetricValue(currentSummary, columnMetadata)
                : "N/A"}
            </span>

            {/* Change */}
            {comparisonDelta !== undefined &&
              (() => {
                const deltaArrow =
                  comparisonDelta > 0 ? "↑" : comparisonDelta < 0 ? "↓" : "";
                const deltaSign = comparisonDelta > 0 ? "+" : "";
                const deltaColor =
                  comparisonDelta > 0
                    ? "var(--d-pos-text)"
                    : comparisonDelta < 0
                    ? "var(--d-neg-text)"
                    : "var(--d-ink-ghost)";

                let formattedDelta: string;
                if (columnMetadata.dataType === "percentage") {
                  const absValue = Math.abs(comparisonDelta);
                  const formatted =
                    absValue < 1 && absValue > 0
                      ? absValue.toFixed(1)
                      : Math.round(absValue).toString();
                  formattedDelta = `${formatted}%`;
                } else {
                  formattedDelta = formatMetricValue(
                    Math.abs(comparisonDelta),
                    columnMetadata
                  );
                }

                return (
                  <>
                    <span style={{ color: "var(--d-ink-ghost)" }}>|</span>
                    <span
                      style={{
                        fontFamily: "var(--d-font-sans)",
                        fontSize: 12.5,
                        color: "var(--d-ink-muted)",
                      }}
                    >
                      Change:
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--d-font-sans)",
                        fontSize: 16,
                        fontWeight: 600,
                        color: deltaColor,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {deltaArrow}
                      {deltaSign}
                      {formattedDelta}
                    </span>
                  </>
                );
              })()}
          </div>

          {/* Expanded Chart - Much Larger */}
          <div className="w-full h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'bar' ? (
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                >
                  {/* SVG Patterns - Same as compact */}
                  <defs>
                    <pattern
                      id="currentStripesExpanded"
                      patternUnits="userSpaceOnUse"
                      width="4"
                      height="4"
                      patternTransform="rotate(45)"
                    >
                      <rect width="4" height="4" fill={CHART_COLORS.current} />
                      <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="4"
                        stroke={CHART_COLORS.currentLight}
                        strokeWidth="2"
                      />
                    </pattern>
                    <pattern
                      id="stlyStripesExpanded"
                      patternUnits="userSpaceOnUse"
                      width="4"
                      height="4"
                      patternTransform="rotate(45)"
                    >
                      <rect width="4" height="4" fill={CHART_COLORS.stly} />
                      <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="4"
                        stroke={CHART_COLORS.stlyLight}
                        strokeWidth="2"
                      />
                    </pattern>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke={CHART_COLORS.grid}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="dayLabel"
                    tick={{
                      fill: CHART_COLORS.text,
                      fontSize: 11,
                      fontFamily: "Geist, Inter, sans-serif",
                    }}
                    axisLine={{ stroke: CHART_COLORS.border }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{
                      fill: CHART_COLORS.text,
                      fontSize: 11,
                      fontFamily: "JetBrains Mono, ui-monospace, monospace",
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(77, 91, 110, 0.08)" }}
                  />

                  {/* STLY Bar (rendered first = behind) */}
                  {showSTLY && data.stly.enabled && (
                    <Bar
                      dataKey="stly"
                      fill="url(#stlyStripesExpanded)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={60}
                    />
                  )}

                  {/* Current Bar (rendered second = in front) */}
                  <Bar
                    dataKey="current"
                    fill="url(#currentStripesExpanded)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              ) : (
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke={CHART_COLORS.grid}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="dayLabel"
                    tick={{
                      fill: CHART_COLORS.text,
                      fontSize: 11,
                      fontFamily: "Geist, Inter, sans-serif",
                    }}
                    axisLine={{ stroke: CHART_COLORS.border }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{
                      fill: CHART_COLORS.text,
                      fontSize: 11,
                      fontFamily: "JetBrains Mono, ui-monospace, monospace",
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ stroke: CHART_COLORS.grid, strokeWidth: 1 }}
                  />

                  {/* STLY Line (rendered first) */}
                  {showSTLY && data.stly.enabled && (
                    <Line
                      type="monotone"
                      dataKey="stly"
                      stroke={CHART_COLORS.stly}
                      strokeWidth={3}
                      dot={{ fill: CHART_COLORS.stly, r: 4 }}
                      activeDot={{ r: 6, fill: CHART_COLORS.stlyLight }}
                      name="STLY"
                      connectNulls
                    />
                  )}

                  {/* Current Line (rendered second) */}
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke={CHART_COLORS.current}
                    strokeWidth={3}
                    dot={{ fill: CHART_COLORS.current, r: 4 }}
                    activeDot={{ r: 6, fill: CHART_COLORS.currentLight }}
                    name="Current"
                    connectNulls
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Custom Legend */}
          <CustomLegend />

          {/* Footer Metadata — mono date range + completeness pip,
              matching `.d-card-meta` cadence from the kit. */}
          <div
            className="pt-4"
            style={{
              borderTop: "1px solid var(--d-rule-soft)",
              fontFamily: "var(--d-font-mono)",
              fontSize: 11,
              letterSpacing: "0.04em",
              color: "var(--d-ink-ghost)",
            }}
          >
            {formatDateDisplay(data.timeDimension.startDate)} to{" "}
            {formatDateDisplay(data.timeDimension.endDate)}
            {(() => {
              const stlyCompleteness =
                data.stly.enabled && data.metadata?.stlyCompleteness
                  ? data.metadata.stlyCompleteness
                  : null;

              if (!stlyCompleteness) return null;

              if (stlyCompleteness.percentage === 100) {
                return <span> • Complete</span>;
              }

              const color =
                stlyCompleteness.percentage < 50
                  ? "var(--d-neg-text)"
                  : "var(--d-warn-text)";

              return (
                <span style={{ color }}>
                  {" "}• Partial ({stlyCompleteness.percentage.toFixed(0)}%)
                </span>
              );
            })()}
          </div>
        </div>
    </Dialog>
  );
}
